/**
 * Client-side, per-user insights cache for Discover → Insights.
 *
 * DANI generation is billed to the individual requesting user's own credit
 * consumption today (see the plan this was built from), so this cache is
 * keyed by `(userKey, category, dataset)` — never shared across users, even
 * when two users resolve to the same category — and lives in `localStorage`
 * rather than a shared DB table, deliberately, for now (see
 * docs note in the PR/plan for the DB-backed alternative this is meant to
 * evolve into).
 */

import type { DiscoverCategorySelection } from './categories';
import type { DiscoverDataSetId } from './dataSets';
import type { DiscoverInsightsResponse } from './types';
import { currentWeekOf } from './weekOf';

export interface CachedInsightsEntry {
    response: DiscoverInsightsResponse;
    /** Flips to true once this batch has actually been displayed to the user. */
    viewed: boolean;
    cachedAt: number;
}

const STORAGE_PREFIX = 'discover-insights-cache:';

export function categorySelectionKey(selection: DiscoverCategorySelection): string {
    switch (selection.kind) {
        case 'preset':
            return `preset:${selection.id}`;
        case 'custom':
            return `custom:${selection.label}`;
        case 'icp':
            return `icp:${selection.icpId}`;
        case 'unknown':
            return 'unknown';
    }
}

function buildKey(userKey: string, selectionKey: string, dataset: DiscoverDataSetId): string {
    return `${STORAGE_PREFIX}${userKey}:${selectionKey}:${dataset}`;
}

/** True if this entry was generated during the current weekly cycle. */
export function isEntryFresh(entry: CachedInsightsEntry): boolean {
    return entry.response.weekOf === currentWeekOf();
}

export function getCachedInsights(
    userKey: string,
    selectionKey: string,
    dataset: DiscoverDataSetId,
): CachedInsightsEntry | null {
    try {
        const raw = window.localStorage.getItem(buildKey(userKey, selectionKey, dataset));
        if (!raw) return null;
        return JSON.parse(raw) as CachedInsightsEntry;
    } catch {
        // Private browsing / storage disabled / corrupt entry — degrade to
        // "always live" rather than throwing.
        return null;
    }
}

export function setCachedInsights(
    userKey: string,
    selectionKey: string,
    dataset: DiscoverDataSetId,
    response: DiscoverInsightsResponse,
): void {
    try {
        const entry: CachedInsightsEntry = { response, viewed: false, cachedAt: Date.now() };
        window.localStorage.setItem(
            buildKey(userKey, selectionKey, dataset),
            JSON.stringify(entry),
        );
    } catch {
        // Best-effort — a failed write just means the next visit is live again.
    }
}

/**
 * The freshest usable batch cached for one user, whatever category/dataset it
 * was fetched for. Backs the Ask DANI landing page's Spotlight widget, which
 * knows who the user is but not which selection lane Spotlight last wrote —
 * an external user's lane is keyed by their resolved ICP, an internal
 * user's by whatever they last previewed. Skips stale weeks and non-'ok'
 * entries; a single corrupt entry is skipped rather than failing the scan.
 *
 * `laneKey` is the winning entry's `userKey:selectionKey:dataset` triple —
 * the same string Spotlight keys its rotation cursor by, so the widget can
 * show the exact 3-insight window the Spotlight page is currently on.
 */
export function getFreshestCachedInsightsForUser(
    userKey: string,
): { entry: CachedInsightsEntry; laneKey: string } | null {
    try {
        const prefix = `${STORAGE_PREFIX}${userKey}:`;
        let best: { entry: CachedInsightsEntry; laneKey: string } | null = null;
        for (let i = 0; i < window.localStorage.length; i++) {
            const key = window.localStorage.key(i);
            if (!key || !key.startsWith(prefix)) continue;
            try {
                const raw = window.localStorage.getItem(key);
                if (!raw) continue;
                const entry = JSON.parse(raw) as CachedInsightsEntry;
                if (!isEntryFresh(entry)) continue;
                if (entry.response.status !== 'ok' || !entry.response.insights?.length) continue;
                if (!best || entry.cachedAt > best.entry.cachedAt) {
                    best = { entry, laneKey: key.slice(STORAGE_PREFIX.length) };
                }
            } catch {
                continue;
            }
        }
        return best;
    } catch {
        // Private browsing / storage disabled — degrade to "nothing cached".
        return null;
    }
}

export function markInsightsViewed(
    userKey: string,
    selectionKey: string,
    dataset: DiscoverDataSetId,
): void {
    try {
        const key = buildKey(userKey, selectionKey, dataset);
        const raw = window.localStorage.getItem(key);
        if (!raw) return;
        const entry = JSON.parse(raw) as CachedInsightsEntry;
        if (entry.viewed) return;
        window.localStorage.setItem(key, JSON.stringify({ ...entry, viewed: true }));
    } catch {
        // Best-effort — worst case, the next login re-generates a bit sooner than needed.
    }
}

/**
 * `mapUserToCategory` (DANI free-text inference from an account name) is not
 * deterministic — the same account can resolve to a differently-worded
 * category label on separate calls. Since the insights cache key above
 * includes the exact label, that non-determinism alone was enough to defeat
 * the cache on every repeat visit. This second cache pins the *mapping*
 * itself to the first label DANI ever returned for a given user, so repeat
 * visits reuse both the mapping and, in turn, the insights cache above.
 * Unlike insights, an account's category isn't week-scoped — it's kept
 * indefinitely rather than checked against `currentWeekOf()`.
 */
const CATEGORY_MAPPING_PREFIX = 'discover-category-mapping:';

function buildCategoryMappingKey(userKey: string): string {
    return `${CATEGORY_MAPPING_PREFIX}${userKey}`;
}

export function getCachedCategoryMapping(userKey: string): string | null {
    try {
        const raw = window.localStorage.getItem(buildCategoryMappingKey(userKey));
        if (!raw) return null;
        const entry = JSON.parse(raw) as { categoryLabel: string };
        return entry.categoryLabel;
    } catch {
        return null;
    }
}

export function setCachedCategoryMapping(userKey: string, categoryLabel: string): void {
    try {
        window.localStorage.setItem(
            buildCategoryMappingKey(userKey),
            JSON.stringify({ categoryLabel, cachedAt: Date.now() }),
        );
    } catch {
        // Best-effort — worst case, the next selection just re-maps live.
    }
}
